import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FieldConfig from './FieldConfig';
import ResultTableRecDetail from './ResultTableRecDetail';
import DiscoverService from '../../services/discoverService';

export default function ResultDetailsNewPage() {
  const { dbkey } = useParams();
  const location = useLocation();
  const toggle = useSelector(state => state.setToggle?.toggle);
  const [snapshot, setSnapshot] = useState(null);
  const [missing, setMissing] = useState(false);
  const [annotStateLocal, setAnnotStateLocal] = useState(null);
  const [ftWindows, setFtWindows] = useState({});

  const snapshotKey = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get('snapshot');
  }, [location.search]);

  useEffect(() => {
    if (!snapshotKey) {
      setMissing(true);
      return;
    }

    // Prefer the opener's in-memory map — avoids localStorage quota limits.
    // Fall back to localStorage for cases where the opener tab was closed
    // before this tab finished loading.
    let parsed = window.opener?.__detailSnapshots?.get?.(snapshotKey) ?? null;

    if (!parsed) {
      const raw = localStorage.getItem(snapshotKey);
      if (!raw) {
        setMissing(true);
        return;
      }
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        console.error('Failed to parse detail snapshot', e);
        setMissing(true);
        return;
      }
    }

    // Hydrate FieldConfig module-level state from the snapshot so column
    // titles, combined fields, and the user's table template match the
    // opener tab.
    const fcState = parsed?.fieldConfigState || {};
    if (fcState.fieldConfig) FieldConfig.fieldConfig = fcState.fieldConfig;
    if (fcState.combinedFields) FieldConfig.combinedFields = fcState.combinedFields;
    if (fcState.userTableTemplate) FieldConfig.userTableTemplate = fcState.userTableTemplate;

    // Restore the global API call context (workflowId / userId / gqApiKey
    // + filter state) so child fetches like the Subject Hit Landscape work.
    if (parsed?.javaCall) {
      window.$javaCall = parsed.javaCall;
    }

    setSnapshot(parsed);
    setAnnotStateLocal(parsed?.annotState || {});

    const cleanup = () => {
      try { localStorage.removeItem(snapshotKey); } catch (e) { /* ignore */ }
      try { window.opener?.__detailSnapshots?.delete?.(snapshotKey); } catch (e) { /* ignore */ }
    };
    window.addEventListener('beforeunload', cleanup);
    return () => window.removeEventListener('beforeunload', cleanup);
  }, [snapshotKey]);

  // Force a re-render after the detail panel mutates annotState in place
  // (e.g. updateNewNotes mutates `annotState.notes[dbkey]` then calls this).
  const refreshAnnotState = useCallback((updated) => {
    setAnnotStateLocal({ ...updated });
  }, []);

  const expandClaim = useCallback((key, val) => {
    setAnnotStateLocal(prev => ({
      ...prev,
      claimsCollapse: { ...(prev?.claimsCollapse || {}), [key]: val },
    }));
  }, []);

  const showLessClaim = useCallback((key, val) => {
    setAnnotStateLocal(prev => ({
      ...prev,
      claimsCollapseLess: { ...(prev?.claimsCollapseLess || {}), [key]: val },
    }));
  }, []);

  const handleOpenFtWindow = useCallback((ftData) => {
    if (!ftData || !ftData.pn) return;
    if (ftWindows[ftData.pn] && !ftWindows[ftData.pn].closed) {
      ftWindows[ftData.pn].focus();
    } else {
      const w = window.open('#/fulldocview/' + encodeURIComponent(ftData.pn));
      if (w) {
        w.starterSeqId = ftData.seqIds;
        w.starterKws = ftData.kws;
        setFtWindows(prev => ({ ...prev, [ftData.pn]: w }));
      }
    }
  }, [ftWindows]);


  const saveAnnotation = useCallback(async (type, choice, annotated, key, value, annotStateArg) => {
    const state = annotStateArg || annotStateLocal || {};
    if (!window.$javaCall || !window.$javaCall.data || !window.$javaCall.data.gqApiKey) {
      console.error('saveAnnotation: missing $javaCall API context');
      return;
    }
    if (choice !== 'single') {
      // Bulk operations need the full results-page context (page list,
      // filter, group, etc.) which this tab does not have.
      alert('Bulk annotation actions ("this page", "all pages", "group") are only available from the main results page.');
      return;
    }

    const callData = { ...window.$javaCall.data };
    if (type === 'checkbox') {
      callData.value = value <= 0 ? 1 : 0;
    } else if (type === 'notes') {
      callData.value = 0;
      callData.comment = value;
    } else {
      callData.value = value;
    }
    callData.annotType = type;
    callData.unannotated = false;
    callData.allPages = false;

    state.checkboxes = state.checkboxes || {};
    state.stars = state.stars || {};
    state.colors = state.colors || {};
    state.notes = state.notes || {};
    if (type === 'checkbox') state.checkboxes[key] = Boolean(value <= 0);
    else if (type === 'star') state.stars[key] = value;
    else if (type === 'color') state.colors[key] = value;
    else if (type === 'notes') state.notes[key] = value;

    callData.selectedIds = [state.dbkeys ? state.dbkeys[key] : key];

    try {
      await DiscoverService.saveAnnotation(callData, callData.gqApiKey);
    } catch (e) {
      console.error('saveAnnotation failed', e);
    }
    setAnnotStateLocal({ ...state });

    // Notify the opener tab so the main results list reflects the change immediately.
    if (window.opener) {
      window.opener.postMessage(
        { type: 'ANNOTATION_UPDATE', annotType: type, key, value: type === 'checkbox' ? Boolean(value <= 0) : value },
        window.location.origin
      );
    }
  }, [annotStateLocal]);

  if (missing) {
    return (
      <div style={{
        padding: 24, maxWidth: 520, margin: '64px auto',
        fontFamily: 'system-ui, sans-serif', textAlign: 'center',
        color: toggle ? '#212529' : '#e5e5e5'
      }}>
        <h3 style={{ marginBottom: 8 }}>Snapshot expired</h3>
        <p style={{ color: toggle ? '#666' : '#aaa' }}>
          Please go back to the main results page and reopen this record in a new tab.
        </p>
      </div>
    );
  }

  if (!snapshot) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  const {
    data, item, idx, filter,
    displayedColumns, isAbWorkflow,
    fromItemsPage, groupValue1, groupValue2,
    parentAnnotState, isCmNoSeq, selectedQueryId
  } = snapshot;
  const annotState = annotStateLocal || snapshot.annotState || {};

  const noop = () => {};
  const highlight = (_fc, _fl, _uf, val) => (val == null || val === '' ? '&nbsp;' : val);

  return (
    <div key={item?.dbkey || dbkey} style={{ padding: 16, minHeight: '100vh', position: 'relative', isolation: 'isolate' }}>
      <ResultTableRecDetail
        key={item?.dbkey || dbkey}
        data={data}
        item={item}
        idx={idx}
        fieldConfig={FieldConfig}
        filter={filter}
        annotState={annotState}
        highlight={highlight}
        toggleFullRecordExpand={noop}
        refreshAnnotState={refreshAnnotState}
        expandAllDetailPage={noop}
        expandAllClaims={noop}
        expandClaim={expandClaim}
        showLessClaim={showLessClaim}
        expandAllAlignments={noop}
        expandAlignment={noop}
        saveAnnotation={saveAnnotation}
        fromItemsPage={fromItemsPage}
        parentAnnotState={parentAnnotState}
        groupValue1={groupValue1}
        groupValue2={groupValue2}
        isAbWorkflow={isAbWorkflow}
        setFtWindowsToOpen={handleOpenFtWindow}
        displayedColumns={displayedColumns}
        currentVisibleSection={null}
        setCurrentVisibleSection={noop}
        isCmNoSeq={isCmNoSeq}
        selectedQueryId={selectedQueryId}
      />
    </div>
  );
}
